import { FRIENDS_QUERY_KEYS } from '@/api/queries/friendship';
import { FEED_QUERY_KEYS } from '@/features/activity/hooks/use-activity';
import { useAuth } from '@/hooks/use-auth';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useProfileByIdentifier } from './use-profile-by-identifier';

/**
 * Shared wiring for the two other-user profile routes
 * (`(root)/profile/[username]` and `(tabs)/profile/[username]`). Both routes
 * resolve the same profile, compute the same `isCurrentUser`, and want the same
 * pull-to-refresh behavior; only their outer wrapper JSX differs (stack vs
 * tab), so that stays inline in each file while all logic lives here.
 */
export function useOtherUserProfile(
  username: string | string[] | undefined
) {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { profileData, isLoading, error, refetch, isRefetching } =
    useProfileByIdentifier(username);

  const isCurrentUser = profileData?.user?.id === currentUser?.id;

  const onRefresh = useCallback(() => {
    refetch();
    queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: FRIENDS_QUERY_KEYS.all });
  }, [refetch, queryClient]);

  const title = profileData?.user?.name || 'Profile';

  return {
    profileData,
    isLoading,
    error,
    isCurrentUser,
    onRefresh,
    refreshing: isRefetching,
    refetch,
    title,
  };
}
